/* Chat assistant — retrieval-augmented Q&A over the knowledge corpus.

   The text sibling of the voice copilot: a floating panel that POSTs the
   question to the backend's gated endpoint ({API}/knowledge/ask), which runs
   hybrid retrieval (vector + keyword + graph, audience-gated per chunk from
   the verified token) and returns a citation-backed answer. No provider keys
   or sockets in the browser — one authed POST per question.

   Renders nothing unless signed in on a site with its own backend
   (SITE.hasBackend — high.surf is frontend-only). */
import React, { useState, useRef, useEffect } from 'react';
import { auth } from '../firebaseConfig';
import { useAuth } from '../AuthContext';
import SITE from '../site';
import './ChatAgent.css';

const API = process.env.REACT_APP_UPLOAD_API || '';

async function authHeaders() {
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ChatAgent() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, busy]);

  async function send(e) {
    e.preventDefault();
    const question = input.trim();
    if (!question || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: question }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/knowledge/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ question }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setMessages((m) => [
        ...m,
        { role: 'agent', text: data.result.text, citations: data.result.citations || [] },
      ]);
    } catch (err) {
      setMessages((m) => [...m, { role: 'agent', text: `Error: ${err.message}`, citations: [] }]);
    } finally {
      setBusy(false);
    }
  }

  if (!API || !user || !SITE.hasBackend) return null;

  return (
    <div className="chat-agent">
      {open && (
        <div className="chat-panel">
          <div className="chat-head">
            <span>Ask the knowledge base</span>
            <button className="chat-close" onClick={() => setOpen(false)}>✕</button>
          </div>
          <div className="chat-log" ref={logRef}>
            {messages.length === 0 && (
              <div className="chat-empty">
                Answers come from the live knowledge corpus with citations — ask
                about spot beta, the house grading style, or anything indexed.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`chat-msg chat-${m.role}`}>
                <div className="chat-text">{m.text}</div>
                {m.citations && m.citations.length > 0 && (
                  <div className="chat-citations">
                    {m.citations.map((c) => (
                      <span key={c.ref} className="chat-cite" title={c.docId}>
                        [{c.ref}] {c.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {busy && <div className="chat-msg chat-agent"><div className="chat-text chat-thinking">thinking…</div></div>}
          </div>
          <form className="chat-input-row" onSubmit={send}>
            <input
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question…"
              disabled={busy}
            />
            <button className="chat-send" type="submit" disabled={busy || !input.trim()}>
              →
            </button>
          </form>
        </div>
      )}
      {!open && (
        <button className="chat-launcher" title="Ask the knowledge base" onClick={() => setOpen(true)}>
          💬
        </button>
      )}
    </div>
  );
}
